// API Configuration for Backend Services
import { ENV } from '@/config/env';

export const API_CONFIG = {
  // Base URLs for services (deployed via nginx)
  // All API requests should use npm.rapidfix.ie domain
  BASE_URL: ENV.API_BASE_URL,
  
  // Service endpoints - ONLY IMPLEMENTED ENDPOINTS
  ENDPOINTS: {
    // User Service Endpoints (IMPLEMENTED)
    USERS: '/users',
    USER_PROFILE: '/users/profile',
    
    // Job Service Endpoints (IMPLEMENTED)
    JOBS: '/jobs',
    JOB_MY_JOBS: '/jobs/my-jobs',
    JOB_SEARCH: '/jobs/search',
    JOB_ASSIGN: '/jobs/{id}/assign',
    JOB_COMPLETE: '/jobs/{id}/complete',
    JOB_CANCEL: '/jobs/{id}/cancel',
    JOB_QUOTES: '/jobs/{id}/quotes',
    
    // Job Categories (UPDATED - New dedicated endpoints)
    JOB_CATEGORIES: '/categories',
    
    // Smart Category Suggestions (NEW)
    CATEGORY_SUGGEST: '/categories/suggest',
    CATEGORY_FEEDBACK: '/categories/feedback', 
    CATEGORY_METRICS: '/categories/metrics',
    
    // Location Service Endpoints (NEW)
    COUNTIES: '/counties',
    COUNTY_BY_ID: '/counties/{id}',
    AREAS: '/areas',
    AREA_BY_ID: '/areas/{id}',
    AREAS_BY_COUNTY: '/areas/by-county/{county_id}',
    
    // Provider Service Endpoints (IMPLEMENTED)
    PROVIDERS: '/providers',
    PROVIDER_SEARCH: '/providers/search',
    PROVIDER_TEAM: '/providers/{id}/team',
    PROVIDER_REFERRALS: '/providers/{id}/referrals',
    PROVIDER_OFFERS: '/providers/{id}/offers',
    PROVIDER_REVIEWS: '/providers/{id}/reviews',
    PROVIDER_AVAILABILITY: '/providers/{id}/availability',
    PROVIDER_CALENDAR: '/providers/{id}/calendar',
    
    // Review Management (IMPLEMENTED via provider service)
    REVIEWS: '/reviews',
    
    // Quote Management (IMPLEMENTED)
    QUOTES: '/quotes',
    
    // Invoice Management (IMPLEMENTED)
    INVOICES: '/invoices',
    
    // Media Management (IMPLEMENTED)
    MEDIA: '/media',
    
    // Analytics (IMPLEMENTED)
    ANALYTICS: '/analytics',
    
    // Communication Service Endpoints (IMPLEMENTED)
    CONVERSATIONS: '/conversations',
    MESSAGES: '/messages',
    NOTIFICATIONS: '/notifications',
    NOTIFICATION_PREFERENCES: '/preferences',
    EMAILS: '/emails',
    WEBSOCKET: '/ws',
    
    // Search Service Endpoints (IMPLEMENTED - via job service)
    SEARCH: '/search',
    SEARCH_JOBS: '/search/jobs',
    SEARCH_SUGGESTIONS: '/search/suggestions',
    SEARCH_POPULAR: '/search/popular',
    SEARCH_FEATURED: '/search/featured',
  },
  
  // Request configuration - minimal headers to avoid CORS preflight
  DEFAULT_HEADERS: {
    'Accept': 'application/json',
  },
  
  // Timeout settings
  TIMEOUT: 10000, // 10 seconds - balanced timeout for better UX
  
  // External API Keys
  HERE_API_KEY: 'A2H3F77rvvzrpFgPwqlJtXDA5xtDrpKvXtTz1Ckhl0k',
};

// Helper function to build full URL with correct service route
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = API_CONFIG.BASE_URL;
  
  // Normalize endpoint - remove leading slash if present
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // If endpoint already starts with api/v1/, it's a full path - just prepend base URL
  if (normalizedEndpoint.startsWith('api/v1/')) {
    return `${baseUrl}/${normalizedEndpoint}`;
  }
  
  // Path-based routing: /api/v1/public/ for public endpoints, /api/v1/auth/ for authenticated endpoints
  if (normalizedEndpoint.startsWith('users')) {
    // User Service: /users -> /api/v1/auth/users (authenticated endpoints)
    // User sync and profile endpoints require authentication
    return `${baseUrl}/api/v1/auth/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('job-templates')) {
    // Job Templates: /job-templates -> /api/v1/public/jobs/templates (public browsing before signup)
    // Convert old format to new format
    const newEndpoint = normalizedEndpoint.replace('job-templates', 'jobs/templates');
    return `${baseUrl}/api/v1/public/${newEndpoint}`;
  } else if (normalizedEndpoint.startsWith('categories')) {
    // Category Service: /categories -> /api/v1/public/categories (all category endpoints are public)
    return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('suggestions')) {
    // Suggestion Service: /suggestions -> /api/v1/public/suggestions (public by default)
    return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('counties') || normalizedEndpoint.startsWith('areas')) {
    // Location Service: /counties, /areas -> /api/v1/public/ (all location endpoints are public)
    return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('providers')) {
    // Provider Service: /providers -> /api/v1/public/providers (public browsing)
    return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('quotes')) {
    // Quote Service: /quotes -> /api/v1/auth/providers/quotes (requires auth)
    return `${baseUrl}/api/v1/auth/providers/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('invoices')) {
    // Invoice Service: /invoices -> /api/v1/auth/providers/invoices (requires auth)
    return `${baseUrl}/api/v1/auth/providers/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('analytics')) {
    // Analytics Service: /analytics -> /api/v1/auth/providers/analytics (requires auth)
    return `${baseUrl}/api/v1/auth/providers/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('media')) {
    // Media Service: /media -> /api/v1/auth/providers/media (requires auth)
    return `${baseUrl}/api/v1/auth/providers/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('conversations') || 
             normalizedEndpoint.startsWith('messages') || 
             normalizedEndpoint.startsWith('notifications') || 
             normalizedEndpoint.startsWith('preferences') ||
             normalizedEndpoint.startsWith('emails')) {
    // Communication Service: /conversations -> /api/v1/auth/communication/conversations (requires auth)
    return `${baseUrl}/api/v1/auth/communication/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('search')) {
    // Search Service: /search -> /api/v1/public/search (public by default)
    return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
  }
  
  // Default fallback - assume it's a public API path
  return `${baseUrl}/api/v1/public/${normalizedEndpoint}`;
};

// Helper function to build authenticated API URL
export const buildAuthApiUrl = (endpoint: string): string => {
  const baseUrl = API_CONFIG.BASE_URL;
  
  // Normalize endpoint - remove leading slash if present
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // Force authenticated routing for specific endpoints
  if (normalizedEndpoint.startsWith('users')) {
    // Authenticated user endpoints: /users -> /api/v1/auth/users
    return `${baseUrl}/api/v1/auth/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('jobs')) {
    // Authenticated job endpoints: /jobs -> /api/v1/auth/jobs
    return `${baseUrl}/api/v1/auth/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('providers')) {
    // Authenticated provider endpoints: /providers -> /api/v1/auth/providers
    return `${baseUrl}/api/v1/auth/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('suggestions/pending-tags')) {
    // Admin suggestion endpoints: /suggestions/pending-tags -> /api/v1/auth/suggestions/pending-tags
    return `${baseUrl}/api/v1/auth/${normalizedEndpoint}`;
  } else if (normalizedEndpoint.startsWith('conversations') || 
             normalizedEndpoint.startsWith('messages') || 
             normalizedEndpoint.startsWith('notifications') || 
             normalizedEndpoint.startsWith('preferences') ||
             normalizedEndpoint.startsWith('emails')) {
    // Communication Service: /conversations -> /api/v1/auth/communication/conversations
    return `${baseUrl}/api/v1/auth/communication/${normalizedEndpoint}`;
  }
  
  // For endpoints that are always authenticated, use auth routing with proper namespace
  if (normalizedEndpoint.startsWith('quotes') || 
      normalizedEndpoint.startsWith('invoices') || 
      normalizedEndpoint.startsWith('analytics') || 
      normalizedEndpoint.startsWith('media')) {
    return `${baseUrl}/api/v1/auth/providers/${normalizedEndpoint}`;
  }
  
  // Default fallback to public
  return buildApiUrl(endpoint);
};

// Helper function to get headers with auth
export const getAuthHeaders = (token?: string): Record<string, string> => {
  const headers: Record<string, string> = { ...API_CONFIG.DEFAULT_HEADERS };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add X-User-ID header if available (required by backend services)
  if (typeof window !== 'undefined') {
    const userId = localStorage.getItem('user_id');
    if (userId) {
      headers['X-User-ID'] = userId;
    }
  }
  
  // Ensure Origin is set for CORS
  if (typeof window !== 'undefined' && !headers['Origin']) {
    headers['Origin'] = window.location.origin;
  }
  
  return headers;
};

// Helper function to validate endpoint format and prevent double paths
export const validateEndpoint = (endpoint: string): string => {
  // Remove any double slashes
  const cleaned = endpoint.replace(/\/+/g, '/');
  
  // Ensure it starts with / for consistency
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
};

// Helper function to build URL with validation
export const buildValidatedApiUrl = (endpoint: string): string => {
  const validatedEndpoint = validateEndpoint(endpoint);
  return buildApiUrl(validatedEndpoint);
};